import type { DdFulfillmentStatus, DdPhaseSummary, DdRequest } from '../api/types';
import { formatDateOnly } from './date-only';

/**
 * The statement a person confirms to mark a checklist item "does not apply"
 * (owner rule 2026-10-05, ADR-0032). The API stores it by version key
 * (`company-never-used-v1`); keep this wording in step with
 * src/modules/dd/index.ts `NA_ATTESTATION_TEXT`.
 */
export const NA_ATTESTATION_TEXT =
  "The information requested here isn't present in, used by, and has never been used in my company.";
/** The version key the API stores for {@link NA_ATTESTATION_TEXT}. */
export const NA_ATTESTATION_KEY = 'company-never-used-v1';

/**
 * Where a checklist item stands — the API's `fulfillmentStatus`, never a client
 * guess. An older server without it falls back to the flags it does send.
 */
export function itemFulfillment(
  req: Pick<DdRequest, 'fulfillmentStatus' | 'provided' | 'notApplicable'>,
): DdFulfillmentStatus {
  if (req.fulfillmentStatus) return req.fulfillmentStatus;
  if (req.notApplicable) return 'not_applicable';
  return req.provided ? 'covered' : 'missing';
}

/** Exact checklist counts: the server's per-phase buckets, added up. The four
 * buckets partition `total`. */
export interface ChecklistCounts {
  total: number;
  covered: number;
  notApplicable: number;
  waitingOnReview: number;
  missing: number;
  /** missing + waiting on review — everything not yet resolved. */
  unresolved: number;
  /** covered + not applicable. */
  resolved: number;
  /** Whole-number share of items resolved, 0–100 (0 when there are none). */
  percent: number;
}

export function checklistCounts(
  phases: readonly Pick<
    DdPhaseSummary,
    'total' | 'covered' | 'notApplicable' | 'waitingOnReview' | 'missing' | 'unresolved'
  >[],
): ChecklistCounts {
  const sum = (pick: (p: (typeof phases)[number]) => number) =>
    phases.reduce((n, p) => n + pick(p), 0);
  const total = sum((p) => p.total);
  const covered = sum((p) => p.covered);
  const notApplicable = sum((p) => p.notApplicable);
  const resolved = covered + notApplicable;
  return {
    total,
    covered,
    notApplicable,
    waitingOnReview: sum((p) => p.waitingOnReview),
    missing: sum((p) => p.missing),
    unresolved: sum((p) => p.unresolved),
    resolved,
    percent: total > 0 ? Math.round((resolved / total) * 100) : 0,
  };
}

/**
 * The checklist's item filters — one per fulfillment state, plus everything.
 * Shared by the admin checklist, the seller checklist and the summary figures
 * that link into them (`?filter=` on the admin checklist URL).
 */
export type ChecklistFilter = 'missing' | 'waiting' | 'covered' | 'na' | 'all';

export const CHECKLIST_FILTERS: readonly ChecklistFilter[] = [
  'missing',
  'waiting',
  'covered',
  'na',
  'all',
];

/** A `?filter=` value as a {@link ChecklistFilter}; null when absent or unknown. */
export function parseChecklistFilter(value: string | null | undefined): ChecklistFilter | null {
  return CHECKLIST_FILTERS.includes(value as ChecklistFilter) ? (value as ChecklistFilter) : null;
}

const FILTER_STATUS: Record<Exclude<ChecklistFilter, 'all'>, DdFulfillmentStatus> = {
  missing: 'missing',
  waiting: 'waiting_on_review',
  covered: 'covered',
  na: 'not_applicable',
};

/** Whether an item in fulfillment state `status` shows under `filter`. */
export function matchesChecklistFilter(
  status: DdFulfillmentStatus,
  filter: ChecklistFilter,
): boolean {
  return filter === 'all' || FILTER_STATUS[filter] === status;
}

/** How many of a phase's items a filter shows, from the server's exact buckets. */
export function phaseFilterCount(
  phase: Pick<DdPhaseSummary, 'total' | 'covered' | 'notApplicable' | 'waitingOnReview' | 'missing'>,
  filter: ChecklistFilter,
): number {
  switch (filter) {
    case 'missing':
      return phase.missing;
    case 'waiting':
      return phase.waitingOnReview;
    case 'covered':
      return phase.covered;
    case 'na':
      return phase.notApplicable;
    default:
      return phase.total;
  }
}

/** Whether a phase has anything to show under `filter` (the "all" filter shows every
 * phase, empty ones included). */
export function phaseHasFilteredItems(
  phase: Parameters<typeof phaseFilterCount>[0],
  filter: ChecklistFilter,
): boolean {
  return filter === 'all' || phaseFilterCount(phase, filter) > 0;
}

/** A phase's date range in words ("Starts Oct 22, 2026 · Due Nov 5, 2026"); null when
 * it has no dates. A phase with only one end says only that. */
export function phaseDateRange(
  phase: Pick<DdPhaseSummary, 'startOn' | 'completeOn'>,
): string | null {
  const parts = [
    phase.startOn ? `Starts ${formatDateOnly(phase.startOn)}` : null,
    phase.completeOn ? `Due ${formatDateOnly(phase.completeOn)}` : null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : null;
}
